import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, CalendarCheck } from "lucide-react";
import { translations, t, type Lang } from "@/lib/translations";

/**
 * Mobile-only sticky CTA bar. Hides until 400px scrolled and when footer
 * enters the viewport. "Schedule" navigates to /?contact=1 which Home
 * picks up to open the existing lead modal.
 */
export default function MobileStickyCTA() {
  const [visible, setVisible] = useState(false);
  const [hiddenByFooter, setHiddenByFooter] = useState(false);
  const location = useLocation();
  const lang: Lang = (typeof document !== "undefined" && document.documentElement.lang === "es" ? "es" : "en");
  const T = translations;

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const footer = document.querySelector("footer");
    if (!footer) return;
    const io = new IntersectionObserver(
      ([entry]) => setHiddenByFooter(entry.isIntersecting),
      { rootMargin: "0px" }
    );
    io.observe(footer);
    return () => io.disconnect();
  }, [location.pathname]);

  // Dispatch event when navigating to home with ?contact=1 — handled by Home
  const target = location.pathname === "/" ? "/?contact=1#hero" : "/?contact=1#hero";

  // Standalone ad landing pages carry their own CTAs — no shared sticky bar.
  if (/^\/(get-started-sd(\/thank-you)?|get-started-gc-4ps-a)$/.test(location.pathname.replace(/\/$/, "")))
    return null;

  if (!visible || hiddenByFooter) return null;

  return (
    <div
      className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-md border-t border-border shadow-[0_-4px_16px_rgba(0,0,0,0.08)]"
      role="region"
      aria-label={t(T.bounce.stickyText, lang)}
    >
      <div className="flex items-stretch gap-2 px-3 py-2">
        <a
          href="tel:+16198154787"
          aria-label={t(T.bounce.stickyCall, lang) + " +1 619-815-4787"}
          className="flex items-center justify-center gap-1.5 px-3 rounded-lg border border-border text-foreground font-heading font-semibold text-sm hover:bg-muted"
        >
          <Phone className="h-4 w-4 text-primary" />
          <span>{t(T.bounce.stickyCall, lang)}</span>
        </a>
        <Link
          to={target}
          aria-label={t(T.bounce.stickyCta, lang)}
          className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground font-heading font-bold text-sm px-4 py-3 shadow-md active:scale-[0.98] transition-transform"
          data-cta="sticky-mobile"
        >
          <CalendarCheck className="h-4 w-4" />
          <span>{t(T.bounce.stickyText, lang)}</span>
        </Link>
      </div>
    </div>
  );
}
